export function parseCoordinatePair(latValue, lngValue) {
  const latText =
    latValue === undefined || latValue === null ? "" : String(latValue).trim();
  const lngText =
    lngValue === undefined || lngValue === null ? "" : String(lngValue).trim();

  if (!latText || !lngText) return null;

  const lat = Number(latText);
  const lng = Number(lngText);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { lat, lng };
}

export function getPersistedMarketplaceUserLocation() {
  try {
    const saved = localStorage.getItem("marketplaceUserLocation");
    if (!saved) return null;

    const { lat, lng } = JSON.parse(saved);
    return parseCoordinatePair(lat, lng);
  } catch {
    return null;
  }
}

export function resolveMarketplaceUserLocation(filters = {}) {
  const fromFilters = parseCoordinatePair(filters.lat, filters.lng);
  if (fromFilters) return fromFilters;

  return getPersistedMarketplaceUserLocation();
}

export function calculateDistanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Light i18n prep: preferred distance unit from localStorage or navigator. */
export function getPreferredDistanceUnit() {
  try {
    const saved = localStorage.getItem("marketplaceDistanceUnit");
    if (saved === "mi" || saved === "km") return saved;
  } catch {
    /* ignore */
  }
  try {
    const lang = (navigator.language || "").toLowerCase();
    if (lang.startsWith("en-us")) return "mi";
  } catch {
    /* ignore */
  }
  return "km";
}

export function formatLocationLabel(location, { approximatePrefix = "Near" } = {}) {
  if (!location) return null;

  const parts = [];
  if (typeof location.city === "string" && location.city.trim()) {
    parts.push(location.city.trim());
  }
  if (typeof location.province === "string" && location.province.trim()) {
    parts.push(location.province.trim());
  }
  let label = null;
  if (parts.length > 0) label = parts.join(", ");
  else if (typeof location.country === "string" && location.country.trim()) {
    label = location.country.trim();
  }

  if (!label) return null;
  if (location.isApproximate) {
    return `${approximatePrefix} ${label}`;
  }
  return label;
}

export function resolveListingDistanceKm(listing, userLocation) {
  const apiDistance = Number(listing?.distance);
  if (Number.isFinite(apiDistance)) {
    return Math.round(apiDistance * 10) / 10;
  }

  const resolvedUserLocation = userLocation || getPersistedMarketplaceUserLocation();
  const userLat = Number(resolvedUserLocation?.lat);
  const userLng = Number(resolvedUserLocation?.lng);
  if (!Number.isFinite(userLat) || !Number.isFinite(userLng)) return null;

  const listingLat = Number(listing?.location?.coordinates?.lat);
  const listingLng = Number(listing?.location?.coordinates?.lng);
  if (!Number.isFinite(listingLat) || !Number.isFinite(listingLng)) return null;

  const distKm = calculateDistanceKm(userLat, userLng, listingLat, listingLng);
  return Math.round(distKm * 10) / 10;
}

export function formatDistanceAway(km, unit = getPreferredDistanceUnit()) {
  if (!Number.isFinite(km)) return null;

  if (unit === "mi") {
    const miles = km * 0.621371;
    const rounded =
      miles >= 10 || Number.isInteger(miles)
        ? String(Math.round(miles))
        : String(Math.round(miles * 10) / 10);
    return `${rounded} mi away`;
  }

  const rounded =
    km >= 10 || Number.isInteger(km)
      ? String(Math.round(km))
      : String(Math.round(km * 10) / 10);

  return `${rounded} km away`;
}

/** Light i18n prep for marketplace prices. */
export function formatMarketplacePrice(amount, currency = "CAD", locale) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;
  try {
    const resolvedLocale =
      locale ||
      (typeof navigator !== "undefined" ? navigator.language : undefined) ||
      "en-CA";
    return new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}
